import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { getCommunity, getCompetitors, getMostCommonCommunityContract, getUserCommunities } from 'apis/CommunityAPI';
import { getApartmentTypes } from 'apis/SettingsAPI';
import { getView } from 'apis/ViewAPI';
import {
  CMA_SUMMARY_INITIAL_FILTERS,
  CMA_SUMMARY_VIEW_FILTER_DISPLAY_COMPETITOR,
  INITIAL_DISPLAYED_NUMBER_OF_COMPETITORS,
} from 'components/CMASummary/constants';
import { areCMASummaryFiltersEqual, transformCMASummaryFiltersInbound } from 'components/CMASummary/filterUtils';
import { CMAData, CMASummaryFilters } from 'components/CMASummary/types';
import { isViewCMA } from 'components/CMASummary/utils';
import { useRootContext } from 'components/layout/RootContext';
import Alert from 'components/lib/Alert';
import Loader from 'components/lib/Loader';
import { canAccessSalesBattlecardContent, canViewPricingData, hasPermissionOrAdmin } from 'helpers/permissions';
import { SNAPSHOT_EDIT_PERMISSIONS } from 'permissions/snapshots';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  handleStoreCommunityMap,
  handleStoreUserCommunities,
  handleStoreUserCommunitiesCompetitors,
} from 'store/slices/globalSlice';
import { processFullHomeCommunities } from 'utils/data/communities';

import { CommunityViewContext } from './CommunityViewContext';
import CommunityViewLayout from './CommunityViewLayout';
import { DEFAULT_FILTERS, VIEW_CATEGORY, VIEW_CLASSNAME } from './constants';
import CommunityViewHeader from './header/CommunityViewHeader';
import { ViewFilters } from './types';
import {
  equalFilters,
  getFiltersFromUrl,
  getSnapshotReminder,
  isViewSalesBattlecard,
  transformFilterInbound,
} from './utils';
import useOverviewContractDefaults from '../../hooks/useOverviewContractDefaults';
import { CommunityContract } from '../../types/contracts';
import { isCommunityCCRC } from '../Community/utils';
import { usePublicationContext } from '../Publication/PublicationContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
  align-items: start;
`;

const ExpandedContainer = styled.div`
  width: 100%;
  display: flex;
  flex-grow: 1;
  flex-flow: column;
`;

interface Props {
  communityId: number;
  competitorId?: number;
  viewId: number;
  preview?: boolean;
  fallbackUrl: string;
}

const CommunityView = ({ communityId, competitorId, viewId, preview = false, fallbackUrl }: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showMessage } = useRootContext();
  const { publication } = usePublicationContext();

  const contentRef = useRef<HTMLDivElement>(null);
  const didInitializeCompareCommunityRef = useRef(false);

  const [currentCommunityId, setCurrentCommunityId] = useState(communityId);
  const [showViewModal, setShowViewModal] = useState<string>('');
  const [view, setView] = useState<any>(undefined);
  const [viewFilters, setViewFilters] = useState<ViewFilters | CMASummaryFilters | undefined>();
  const [filtersModified, setFiltersModified] = useState<boolean>(false);
  const [CMASummaryData, setCMASummaryData] = useState<CMAData | undefined>(undefined);
  const [handleDownloadCSV, setHandleDownloadCSV] = useState<() => void>(() => () => {
    // Do Nothing
  });
  const triggerExpandAllRef = useRef<() => void>(() => {
    // Do Nothing
  });
  const triggerCollapseAllRef = useRef<() => void>(() => {
    // Do Nothing
  });

  const { data: community } = useQuery({
    queryKey: ['community', currentCommunityId, publication?.hash],
    queryFn: () =>
      getCommunity(currentCommunityId, { ...(publication?.hash ? { public_hash: publication.hash } : {}) }),
    refetchOnWindowFocus: false,
    enabled: currentCommunityId > 0,
  });

  const { data: competitors, isFetched: isCompetitorsFetched } = useQuery({
    queryKey: ['competitors', currentCommunityId, publication?.hash],
    queryFn: () =>
      getCompetitors(currentCommunityId, { ...(publication?.hash ? { public_hash: publication.hash } : {}) }),
    refetchOnWindowFocus: false,
    enabled: currentCommunityId > 0,
  });

  const { data: apartmentTypes = [] } = useQuery({
    queryKey: ['apartmentTypes', community, competitors],
    queryFn: () => {
      return getApartmentTypes({
        communities: [currentCommunityId, ...(competitors ?? []).map((item: any) => item?.id)],
      });
    },
    refetchOnWindowFocus: false,
    enabled: currentCommunityId > 0,
  });

  useQuery({
    queryKey: [publication, community?.company?.id, competitorId],
    queryFn: () =>
      getUserCommunities({
        companies: [community.company.id],
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
      }),
    onSuccess: (response) => {
      const { communities, competitors, communityMap } = processFullHomeCommunities(response);
      dispatch(handleStoreCommunityMap(communityMap));
      dispatch(handleStoreUserCommunities(communities));
      dispatch(handleStoreUserCommunitiesCompetitors(competitors));
    },
    enabled: !!publication?.hash && !!competitorId && !!community?.company?.id,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const {
    data: viewData,
    isLoading: isViewLoading,
    isFetched: isViewFetched,
    isError,
    refetch: viewRefetch,
  } = useQuery({
    queryKey: ['view', viewId, currentCommunityId, competitorId, publication],
    queryFn: () =>
      getView(viewId, {
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
      }),
    enabled: !!viewId && viewId > 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { data: mostCommonCommunityContract } = useQuery<CommunityContract>({
    queryKey: ['mostCommonCommunityContracts', currentCommunityId, publication?.hash],
    queryFn: () =>
      getMostCommonCommunityContract(currentCommunityId, {
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
      }),
    enabled: Boolean(currentCommunityId) && Boolean(community) && isCommunityCCRC(community?.rental_types),
    refetchOnWindowFocus: false,
  });

  const handleUpdateNow = () => {
    navigate(`/community/${communityId}/info?openSnapshotEditor=1`);
  };

  useEffect(() => {
    setCurrentCommunityId(communityId);
    didInitializeCompareCommunityRef.current = false;
  }, [communityId, competitorId, viewId]);

  useEffect(() => {
    if (viewData) {
      const isPublicView = Boolean(publication?.hash);
      const isPricingRestrictedView =
        viewData.category === VIEW_CATEGORY.CMA_SUMMARY || viewData.category === VIEW_CATEGORY.SALES_BATTLECARD;

      if (!isPublicView && isPricingRestrictedView && !canViewPricingData(currentUser)) {
        showMessage('error', 'This view is not available for your account.');
        navigate(fallbackUrl);
        return;
      }

      if (!isPublicView && isViewSalesBattlecard(viewData) && !canAccessSalesBattlecardContent(currentUser)) {
        showMessage('error', 'Sales Battlecard is not available for your account.');
        navigate(fallbackUrl);
        return;
      }

      const communities = viewData?.filter?.communities;
      if (
        !didInitializeCompareCommunityRef.current &&
        competitorId &&
        Array.isArray(communities) &&
        communities.length > 0
      ) {
        if (currentCommunityId !== communities[0]) {
          setCurrentCommunityId(communities[0]);
          didInitializeCompareCommunityRef.current = true;
          return;
        }
        didInitializeCompareCommunityRef.current = true;
      }
      setView(viewData);
      if (isViewCMA(viewData)) {
        let filtersData: CMASummaryFilters = CMA_SUMMARY_INITIAL_FILTERS;

        if (viewData.filter) {
          filtersData = transformCMASummaryFiltersInbound(viewData.filter);
        }

        if (competitors?.length > INITIAL_DISPLAYED_NUMBER_OF_COMPETITORS) {
          filtersData = {
            ...filtersData,
            viewOptions: {
              ...filtersData.viewOptions,
              competitorDisplayMode: CMA_SUMMARY_VIEW_FILTER_DISPLAY_COMPETITOR.PAGINATE,
            },
          };
        }

        if (competitorId) {
          filtersData = {
            ...filtersData,
            viewOptions: {
              ...filtersData.viewOptions,
              averageCompetitors: false,
            },
          };
        }

        setViewFilters(filtersData);
      } else {
        let transformedFilters = transformFilterInbound(viewData?.filter);
        const urlFilters = getFiltersFromUrl();
        if (urlFilters) {
          transformedFilters = urlFilters;
        }

        if (competitorId) {
          setViewFilters(
            Object.assign({}, transformedFilters, { competitors: [competitorId], communities: [currentCommunityId] })
          );
        } else {
          setViewFilters(transformedFilters);
        }
      }
    } else if (isViewFetched && isError) {
      showMessage('error', 'View could not be found!');
      navigate(fallbackUrl);
    }
  }, [viewData, isViewFetched, isError, competitors, competitorId, currentCommunityId, currentUser]);

  useEffect(() => {
    if (view && viewFilters) {
      if (isViewCMA(view)) {
        const baseFilters = view.filter ? transformCMASummaryFiltersInbound(view.filter) : CMA_SUMMARY_INITIAL_FILTERS;
        setFiltersModified(!areCMASummaryFiltersEqual(viewFilters as CMASummaryFilters, baseFilters));
      } else {
        setFiltersModified(!equalFilters(view.filter, viewFilters));
      }
    }
  }, [view, viewFilters]);

  const isFetched = isViewFetched;
  const isLoading = isViewLoading;

  useOverviewContractDefaults({
    community,
    competitors,
    viewFilters,
    setViewFilters,
  });

  const snapshotReminder = getSnapshotReminder(community?.latest_snapshot_date);
  const isPublicView = publication?.hash;
  const showSnapshotReminder = community && snapshotReminder && !competitorId && !isPublicView;
  const showUpdateButton = hasPermissionOrAdmin(currentUser, SNAPSHOT_EDIT_PERMISSIONS);
  const alertActions = showUpdateButton
    ? [{ dataTestId: 'banner_update_pricing_update_now_link', label: 'Update now', onClick: handleUpdateNow }]
    : [];

  return (
    <CommunityViewContext.Provider
      value={{
        contentRef,
        viewId,
        view,
        viewFilters,
        filtersModified,
        communityId: currentCommunityId,
        community,
        competitorId,
        competitors,
        isCompetitorsFetched,
        apartmentTypes,
        CMASummaryData,
        mostCommonCommunityContract,
        setCMASummaryData,
        triggerExpandAllRef,
        triggerCollapseAllRef,
        setView,
        showViewModal,
        handleDownloadCSV,
        setHandleDownloadCSV,
        isViewCMA: isViewCMA(viewData),
        isViewSalesBattlecard: isViewSalesBattlecard(viewData),
        setShowViewModal,
        setViewFilters,
        setCommunityId: setCurrentCommunityId,
        viewRefetch,
        preview,
      }}
    >
      <Container>
        <Loader active={isLoading} />
        {isFetched && (
          <ExpandedContainer ref={contentRef} className={VIEW_CLASSNAME}>
            {showSnapshotReminder && (
              <Alert
                dataTestId="banner_update_pricing_text"
                type={snapshotReminder.type}
                message={snapshotReminder.message}
                showIcon
                actions={alertActions}
              />
            )}
            <CommunityViewHeader />
            <CommunityViewLayout data={viewData} />
          </ExpandedContainer>
        )}
      </Container>
    </CommunityViewContext.Provider>
  );
};

export default CommunityView;
